import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router";
import { getStatusCodeFromError } from "@entrilia/shared/api/axiosHelper";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import { wait } from "@entrilia/shared/utilities/promiseHelper";
import biApi from "../../../../../api/biApi";
import { selectTemplates } from "../store/selectors/reportTemplatesSelectors";
import type { AppDispatch } from "../store/store";
import { applyTemplate } from "../store/thunks/currentReportThunks";

const templateIdQueryParamKey = "templateId";

export const useApplyTemplate = () => {
  const { sendNotification } = useNotificationContext();
  const navigate = useNavigate();

  const dispatch: AppDispatch = useDispatch();
  const templates = useSelector(selectTemplates);

  const [showTemplateNotFound, setShowTemplateNotFound] = useState(false);
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [showTemplateFailedToApply, setShowTemplateFailedToApply] = useState(false);

  const [cachedTemplateId, setCachedTemplateId] = useState<string>();
  const [searchParams] = useSearchParams();

  const templateId = searchParams.get(templateIdQueryParamKey);
  const applyingTemplateName = templates.find((template) => template.reportId === cachedTemplateId)?.name;

  const handleCloseFailedToApplyDialog = useCallback(() => {
    setCachedTemplateId(undefined);
    setShowTemplateFailedToApply(false);
  }, []);

  const handleTryAgain = useCallback(() => {
    setShowTemplateFailedToApply(false);
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.set(templateIdQueryParamKey, cachedTemplateId || "");
    navigate("?" + nextSearchParams.toString());
  }, [cachedTemplateId, navigate, searchParams]);

  useEffect(() => {
    const getTemplate = async () => {
      if (templateId !== null) {
        setCachedTemplateId(templateId);
        const nextSearchParams = new URLSearchParams(searchParams);
        nextSearchParams.delete(templateIdQueryParamKey);
        navigate("?" + nextSearchParams.toString());
        setLoadingTemplate(true);
        try {
          const getTemplatePromise = biApi.getReportTemplateForCompany({ templateId: templateId });
          const [templateResponse] = await Promise.all([getTemplatePromise, wait(3000)]);
          if (templateResponse.success && templateResponse.data !== undefined) {
            dispatch(applyTemplate(templateResponse.data));
            sendNotification("Template applied");
          } else {
            setShowTemplateNotFound(true);
          }
        } catch (e) {
          if (getStatusCodeFromError(e) === 404) {
            setShowTemplateNotFound(true);
          } else {
            setShowTemplateFailedToApply(true);
          }
        } finally {
          setLoadingTemplate(false);
        }
      }
    };
    getTemplate();
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [templateId]);

  return {
    loadingTemplate,
    showTemplateNotFound,
    showTemplateFailedToApply,
    applyingTemplateName,
    handleCloseFailedToApplyDialog,
    handleTryAgain,
  };
};
